import apiFetch from '../../../utils/apiFetch';
import { getCharacterLanguages, LANGUAGE_OPTIONS } from '../../../shared/rules/languages.mjs';
import { DEFT_EXPLORER_LANGUAGE_COUNT, needsDeftExplorerLanguages } from '../../../shared/rules/ranger.mjs';
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Modal, Form, Alert } from "react-bootstrap";
import DockControls from '../components/DockControls';
import { experienceProfile } from '../../../shared/rules/experience.mjs';
import LevelUp from "./LevelUp";
import { ModalShell, ModalHeader, ModalBody, ModalFooter, Section, Button } from "../common/HudPrimitives";
import proficiencyBonus from '../../../utils/proficiencyBonus';
import { getCharacterTotalLevel } from "./characterProgression";
import SubclassChooser from './SubclassChooser';
import { normalizeClassId, getClassLevel } from '../../../shared/rules/classIdentity.mjs';
import { derivePendingSubclassChoices, getSelectedSubclassId, getSubclass } from '../../../shared/rules/subclasses/index.mjs';

export default function CharacterInfo({
  form,
  onCharacterChange,
  show,
  handleClose,
  onShowBackground,
  isDocked = false,
  dockedSide = null,
  onDockClose,
  onDockChange,
  characterFigurine,
  handleOpenTokenPicker,
  tokenPickerSaving = false,
}) {
  const [languageChoice, setLanguageChoice] = useState('');
  const [savedLanguage, setSavedLanguage] = useState(null);
  const [languageSaving, setLanguageSaving] = useState(false);
  const [languageError, setLanguageError] = useState('');
  const [rangerLanguageChoices, setRangerLanguageChoices] = useState(() => Array(DEFT_EXPLORER_LANGUAGE_COUNT).fill(''));
  const [savedRangerLanguages, setSavedRangerLanguages] = useState(null);
  useEffect(() => {
    setLanguageChoice(''); setSavedLanguage(null); setLanguageError('');
    setRangerLanguageChoices(Array(DEFT_EXPLORER_LANGUAGE_COUNT).fill('')); setSavedRangerLanguages(null);
  }, [form._id]);
  const totalLevel = getCharacterTotalLevel(form);
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [selectedOverrides, setSelectedOverrides] = useState({});
  const [choosingClassId, setChoosingClassId] = useState(null);
  const effectiveForm = useMemo(() => {
    const classState = { ...(form.classState || {}) };
    Object.entries(selectedOverrides).forEach(([classId, subclassId]) => { classState[classId] = { ...(classState[classId] || {}), subclassId }; });
    return { ...form, classState };
  }, [form, selectedOverrides]);
  const pendingChoices = derivePendingSubclassChoices(effectiveForm);

  const handleShowLevelUpModal = () => {
    setShowLevelUpModal(true);
  };

  const handleCloseLevelUpModal = () => {
    setShowLevelUpModal(false);
  };

  const selectedLanguage = form.classState?.rogue?.bonusLanguage || savedLanguage;
  const rogueLanguageForm = selectedLanguage ? { ...form, classState: { ...form.classState, rogue: { ...form.classState?.rogue, bonusLanguage: selectedLanguage } } } : form;
  const languageForm = savedRangerLanguages ? { ...rogueLanguageForm, classState: { ...rogueLanguageForm.classState, ranger: { ...rogueLanguageForm.classState?.ranger, deftExplorerLanguages: savedRangerLanguages } } } : rogueLanguageForm;
  const showRangerLanguages = needsDeftExplorerLanguages(languageForm);
  const knownLanguages = getCharacterLanguages(languageForm);
  const raceLanguages = knownLanguages.join(', ');
  const languageOptions = LANGUAGE_OPTIONS.filter(language => !knownLanguages.includes(language));
  const saveLanguage = async () => {
    if (languageSaving || !languageChoice) return;
    setLanguageSaving(true); setLanguageError('');
    try {
      const response = await apiFetch(`/characters/${form._id}/classes/rogue/language`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ language: languageChoice }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || 'Could not save language.');
      setSavedLanguage(payload.language);
      onCharacterChange?.(previous => ({ ...previous, classState: { ...previous.classState, rogue: { ...previous.classState?.rogue, bonusLanguage: payload.language } } }));
    } catch (error) { setLanguageError(error.message); }
    finally { setLanguageSaving(false); }
  };
  const rangerLanguagesReady = rangerLanguageChoices.every(Boolean) && new Set(rangerLanguageChoices).size === rangerLanguageChoices.length;
  const saveRangerLanguages = async () => {
    if (languageSaving || !rangerLanguagesReady) return;
    setLanguageSaving(true); setLanguageError('');
    try {
      const response = await apiFetch(`/characters/${form._id}/classes/ranger/languages`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ languages: rangerLanguageChoices }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || 'Could not save languages.');
      setSavedRangerLanguages(payload.languages);
      onCharacterChange?.(previous => ({ ...previous, classState: { ...previous.classState, ranger: { ...previous.classState?.ranger, deftExplorerLanguages: payload.languages } } }));
    } catch (error) { setLanguageError(error.message); }
    finally { setLanguageSaving(false); }
  };

  const dialogClassName = useMemo(() => {
    if (!isDocked) {
      return undefined;
    }

    const classes = ['docked-modal'];
    if (dockedSide) {
      classes.push(`docked-modal--${dockedSide}`);
    }
    classes.push('docked-modal--characterInfo');
    return classes.join(' ');
  }, [isDocked, dockedSide]);

  const modalClassName = useMemo(() => {
    const classes = ['dnd-modal', 'modern-modal'];
    if (isDocked) {
      classes.push('docked-modal-container');
    }
    return classes.join(' ');
  }, [isDocked]);

  const handleModalHide = useCallback(() => {
    if (isDocked) {
      if (typeof onDockClose === 'function') {
        onDockClose();
      }
      return;
    }

    handleClose?.();
  }, [handleClose, isDocked, onDockClose]);

  const raceName = form?.race?.name?.toLowerCase?.();
  const isDragonborn = raceName === 'dragonborn';
  const isGoliath = raceName === 'goliath';
  const isElf = raceName === 'elf';
  const isTiefling = raceName === 'tiefling';
  const dragonAncestries = isDragonborn ? form?.race?.dragonAncestries || {} : {};
  const giantAncestries = isGoliath ? form?.race?.giantAncestries || {} : {};
  const elvenLineages = isElf ? form?.race?.elvenLineages || {} : {};
  const tieflingLegacies = isTiefling
    ? form?.race?.fiendishLegacies || {}
    : {};
  const goliathAncestry = isGoliath
    ? form?.race?.selectedAncestry ||
      (form?.race?.selectedAncestryKey && giantAncestries
        ? giantAncestries[form.race.selectedAncestryKey]
        : null) ||
      form?.giantAncestry ||
      (form?.giantAncestryKey && giantAncestries
        ? giantAncestries[form.giantAncestryKey]
        : null)
    : null;
  const goliathAncestryName = goliathAncestry
    ? goliathAncestry.ancestryName ||
      goliathAncestry.name ||
      goliathAncestry.label ||
      "Giant Ancestry"
    : null;
  const dragonbornAncestry = isDragonborn
    ? form?.race?.selectedAncestry ||
      (form?.race?.selectedAncestryKey && dragonAncestries
        ? dragonAncestries[form.race.selectedAncestryKey]
        : null) ||
      form?.dragonAncestry ||
      (form?.dragonAncestryKey && dragonAncestries
        ? dragonAncestries[form.dragonAncestryKey]
        : null)
    : null;
  const dragonbornAncestryName = dragonbornAncestry
    ? dragonbornAncestry.label ||
      dragonbornAncestry.ancestryName ||
      dragonbornAncestry.name ||
      form?.dragonAncestryKey ||
      form?.race?.selectedAncestryKey ||
      "Draconic Ancestry"
    : null;
  const elvenLineage = isElf
    ? form?.race?.selectedAncestry ||
      (form?.race?.selectedAncestryKey && elvenLineages
        ? elvenLineages[form.race.selectedAncestryKey]
        : null) ||
      form?.elvenLineage ||
      (form?.elvenLineageKey && elvenLineages
        ? elvenLineages[form.elvenLineageKey]
        : null)
    : null;
  const elvenLineageName = elvenLineage
    ? elvenLineage.label || elvenLineage.name || 'Elven Lineage'
    : null;
  const tieflingLegacy = isTiefling
    ? form?.race?.selectedAncestry ||
      (form?.race?.selectedAncestryKey && tieflingLegacies
        ? tieflingLegacies[form.race.selectedAncestryKey]
        : null) ||
      form?.tieflingLegacy ||
      (form?.tieflingLegacyKey && tieflingLegacies
        ? tieflingLegacies[form.tieflingLegacyKey]
        : null)
    : null;
  const tieflingLegacyName = tieflingLegacy
    ? tieflingLegacy.label || tieflingLegacy.name || 'Fiendish Legacy'
    : null;
  const tieflingLegacyAbility = (() => {
    const fromForm =
      typeof form?.tieflingLegacyAbility === 'string'
        ? form.tieflingLegacyAbility.trim()
        : '';
    if (fromForm) {
      return fromForm;
    }
    const fromRace =
      typeof form?.race?.selectedLineageAbility === 'string'
        ? form.race.selectedLineageAbility.trim()
        : '';
    return fromRace;
  })();
  const tieflingLegacyResistance = (() => {
    const fromRace =
      typeof form?.race?.selectedFiendishLegacyResistance === 'string'
        ? form.race.selectedFiendishLegacyResistance.trim()
        : '';
    if (fromRace) {
      return fromRace;
    }
    const fromLegacy =
      typeof tieflingLegacy?.resistance === 'string'
        ? tieflingLegacy.resistance.trim()
        : '';
    return fromLegacy;
  })();
  const tieflingLegacySubtext = (() => {
    if (!isTiefling) {
      return null;
    }
    const parts = [];
    if (tieflingLegacyName) {
      parts.push(tieflingLegacyName);
    }
    if (tieflingLegacyResistance) {
      parts.push(`Resistance: ${tieflingLegacyResistance}`);
    }
    if (tieflingLegacyAbility) {
      parts.push(`Spellcasting Ability: ${tieflingLegacyAbility}`);
    }
    return parts.length ? parts.join(' • ') : null;
  })();
  const displaySize =
    form?.temporarySize || form?.size || form?.height || "—";
  const hasFigurineSelection = Boolean(
    characterFigurine?.figurineImageUrl || characterFigurine?.figurineImagePublicId
  );
  const canOpenTokenPicker = typeof handleOpenTokenPicker === 'function';
  const isFigurineButtonDisabled = tokenPickerSaving || !canOpenTokenPicker;

  const figurineButtonLabel = tokenPickerSaving
    ? 'Updating Figurine...'
    : hasFigurineSelection
    ? 'Change Figurine'
    : 'Choose Figurine';

  const handleFigurineButtonClick = useCallback(() => {
    if (tokenPickerSaving || !canOpenTokenPicker) {
      return;
    }

    handleOpenTokenPicker();
  }, [canOpenTokenPicker, handleOpenTokenPicker, tokenPickerSaving]);

  const sortedClasses = useMemo(() => {
    const classes = new Map();
    (form.occupation || []).forEach((entry) => {
      const label = entry.Occupation ?? entry.Name ?? entry.occupation ?? entry.name ?? entry.class;
      const classId = normalizeClassId(label);
      if (!classId) return;
      const current = classes.get(classId);
      classes.set(classId, { classId, label: current?.label || label, Level: (current?.Level || 0) + Number(entry.Level ?? entry.level ?? 0) });
    });
    return [...classes.values()].sort((a, b) => b.Level - a.Level);
  }, [form.occupation]);
  const characterName = form.name || form.characterName || 'Unnamed Hero';

  const speciesDetail = [goliathAncestryName, dragonbornAncestryName, elvenLineageName, tieflingLegacySubtext].filter(Boolean).join(' • ');
  const classSummary = sortedClasses.map((entry) => `${entry.label} ${entry.Level}`).join(' / ');
  const summaryLine = [form.race?.name, classSummary, form.background?.name && `${form.background.name} background`].filter(Boolean).join(' · ');
  const experience = experienceProfile(form);
  // XP only gates levelling inside a campaign; outside one the DM levels characters directly.
  const tracksExperience = Boolean(form.campaign);
  const levelUpLocked = tracksExperience && !experience.ready;
  const profileRows = [
    { label: 'Species', value: form.race?.name || '—', detail: speciesDetail },
    { label: 'Background', value: form.background?.name || '—', action: onShowBackground && <Button variant="ghost" size="sm" onClick={onShowBackground} aria-label="Show Background" className="overview-profile__info"><i className="fa-solid fa-circle-info" aria-hidden="true"></i></Button> },
    { label: 'Languages', value: raceLanguages || '—' },
    { label: 'Size', value: displaySize },
    { label: 'Age', value: form.age || '—' },
    { label: 'Sex', value: form.sex || '—' },
    { label: 'Weight', value: form.weight ? `${form.weight} lb` : '—' },
  ];

  return (
    <Modal
      className={`${modalClassName} character-info-modal`}
      show={show}
      onHide={handleModalHide}
      size="xl"
      centered={!isDocked}
      scrollable
      backdrop={isDocked ? false : true}
      enforceFocus={!isDocked}
      restoreFocus={!isDocked}
      dialogClassName={dialogClassName}
    >
      <ModalShell className="character-info-shell">
        <ModalHeader
          actions={
            <DockControls
              dockedSide={dockedSide}
              onDockChange={onDockChange}
              isDocked={isDocked}
            />
          }
        >
          <div className="character-summary-header">
            {/* The token doubles as the figurine picker, replacing the separate Figurine card. */}
            <button type="button" className="character-summary-header__portrait overview-portrait" onClick={handleFigurineButtonClick} disabled={isFigurineButtonDisabled} aria-label={figurineButtonLabel} title={figurineButtonLabel}>
              {characterFigurine?.figurineImageUrl ? (
                <img src={characterFigurine.figurineImageUrl} alt="Selected figurine token" />
              ) : (
                <i className="fas fa-chess-king" aria-hidden="true"></i>
              )}
              {canOpenTokenPicker && <span className="overview-portrait__edit" aria-hidden="true">{tokenPickerSaving ? '…' : 'Edit'}</span>}
            </button>
            <div className="character-summary-header__copy">
              <h3>{characterName}</h3>
              <p>{summaryLine || 'Adventurer'}</p>
              {!hasFigurineSelection && <small>No figurine selected</small>}
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="character-info-body">
          <div className="overview-layout">
            <Section className="overview-card overview-profile">
              <h4 className="overview-card__title">Profile</h4>
              <dl className="overview-profile__list">
                {profileRows.map((row) => (
                  <div key={row.label} className="character-info-item overview-profile__row">
                    <dt>{row.label}</dt>
                    <dd>
                      <span className="overview-profile__value">{row.value}{row.action}</span>
                      {row.detail && <small>{row.detail}</small>}
                      {row.label === 'Languages' && getClassLevel(form, 'rogue') >= 1 && !selectedLanguage && <div className="overview-profile__language">
                        <Form.Label htmlFor="rogue-language">Thieves’ Cant: choose one additional language</Form.Label>
                        <Form.Select id="rogue-language" size="sm" value={languageChoice} onChange={event => setLanguageChoice(event.target.value)} disabled={languageSaving}>
                          <option value="">Select language</option>
                          {languageOptions.map(language => <option key={language} value={language}>{language}</option>)}
                        </Form.Select>
                        <Button size="sm" onClick={saveLanguage} disabled={!languageChoice || languageSaving || !form._id}>{languageSaving ? 'Saving…' : 'Save language'}</Button>
                      </div>}
                      {row.label === 'Languages' && showRangerLanguages && <div className="overview-profile__language">
                        <span className="form-label">Deft Explorer: choose two more languages</span>
                        {rangerLanguageChoices.map((choice, index) => (
                          <Form.Select key={index} size="sm" aria-label={`Deft Explorer language ${index + 1}`} value={choice} disabled={languageSaving}
                            onChange={event => setRangerLanguageChoices(previous => previous.map((entry, i) => (i === index ? event.target.value : entry)))}>
                            <option value="">Select language</option>
                            {languageOptions.map(language => <option key={language} value={language} disabled={rangerLanguageChoices.includes(language) && choice !== language}>{language}</option>)}
                          </Form.Select>
                        ))}
                        <Button size="sm" onClick={saveRangerLanguages} disabled={!rangerLanguagesReady || languageSaving || !form._id}>{languageSaving ? 'Saving…' : 'Save languages'}</Button>
                      </div>}
                      {row.label === 'Languages' && languageError && <Alert variant="danger">{languageError}</Alert>}
                    </dd>
                  </div>
                ))}
              </dl>
            </Section>

            <Section className="overview-card overview-progression">
              <h4 className="overview-card__title">Progression</h4>
              <ul className="overview-classes" aria-label="Character classes">
                {sortedClasses.length ? sortedClasses.map((el) => {
                  const subclassId = getSelectedSubclassId(effectiveForm, el.classId);
                  const subclassName = getSubclass(el.classId, subclassId)?.name;
                  const canChooseSubclass = pendingChoices.some((choice) => choice.classId === el.classId);
                  return (
                    <li key={el.classId} className="overview-classes__row">
                      <span className="overview-classes__name">
                        <strong>{el.label}</strong>
                        {(subclassName || canChooseSubclass) && <small>{subclassName || 'Subclass choice available'}</small>}
                      </span>
                      {canChooseSubclass && <Button variant="ghost" size="sm" onClick={() => setChoosingClassId(el.classId)}>Choose Subclass</Button>}
                      <span className="overview-classes__level">Level {el.Level}</span>
                    </li>
                  );
                }) : <li className="character-info-empty">No classes recorded.</li>}
              </ul>
              {choosingClassId && <SubclassChooser characterId={form._id || form.id} classId={choosingClassId} classLevel={getClassLevel(effectiveForm, choosingClassId)} onSelected={({ subclassId }) => { const classId = choosingClassId; setSelectedOverrides(current => ({ ...current, [classId]: subclassId })); onCharacterChange?.(previous => ({ ...previous, classState: { ...previous.classState, [classId]: { ...previous.classState?.[classId], subclassId } } })); setChoosingClassId(null); }} />}
              <div className="overview-progression__row">
                <span>Proficiency bonus</span>
                <strong>+{proficiencyBonus(totalLevel)}</strong>
              </div>
              {tracksExperience && experience.next !== null && (
                <div className="overview-xp">
                  <div className="overview-progression__row">
                    <span>Experience</span>
                    <strong>{experience.total.toLocaleString()} / {experience.next.toLocaleString()} XP</strong>
                  </div>
                  <div className="overview-xp__bar" role="progressbar" aria-label="Experience toward next level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(experience.progress * 100)}>
                    <span style={{ width: `${Math.round(experience.progress * 100)}%` }} />
                  </div>
                </div>
              )}
              <div className="overview-levelup">
                <span>{levelUpLocked ? `${(experience.next - experience.total).toLocaleString()} XP to level ${totalLevel + 1}` : `Ready for level ${totalLevel + 1}`}</span>
                <Button className="progression-entry-button" variant="primary" disabled={levelUpLocked} title={levelUpLocked ? 'Earn XP to unlock the next level' : undefined} onClick={handleShowLevelUpModal}>Level Up</Button>
              </div>
            </Section>
          </div>
        </ModalBody>
        <ModalFooter className="character-info-footer">
          <Button variant="ghost" onClick={handleModalHide}>Close</Button>
        </ModalFooter>
      </ModalShell>
      <LevelUp show={showLevelUpModal} handleClose={handleCloseLevelUpModal} form={form} />
    </Modal>
  );
}

